import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlay } from '@fortawesome/free-solid-svg-icons';

import { useAuth } from './authentication/AuthContext';

/**
 * LE LANCEMENT D'UNE SÉANCE.
 *
 * Trois choses s'y décident, et elles n'ont pas le même poids :
 *
 * 1. LES RÔLES, qui engagent la partie — leur nombre fixe le budget de
 *    décisions (`custom_game_function.init_cg_game` : `vote = somme des
 *    acteurs`, puis un facteur de rééquilibrage). Aucun rôle sélectionné dans
 *    un atelier qui en déclare, et la séance part avec un budget nul : toutes
 *    les mesures coûtent 1 pour 0 disponible. D'où le seul blocage de cet
 *    écran.
 * 2. LA FICHE DE SÉANCE, entièrement FACULTATIVE, et modifiable après coup
 *    depuis « Mes séances » (api/session_history.py). Elle est ici parce que
 *    l'effectif et le client se savent en salle, à l'instant du lancement — les
 *    retrouver trois semaines plus tard est exactement ce qui ne se fait pas.
 * 3. La séance de test, qui la retire des statistiques et du classement.
 */

const dateLabel = () => new Date().toLocaleDateString('fr-FR', {
  day: 'numeric', month: 'long', year: 'numeric',
});

const GameConfigurator = ({ gameConfigId }) => {
  const [gameConfig, setGameConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selectedActors, setSelectedActors] = useState([]);
  const [gameName, setGameName] = useState('');
  // La fiche de séance, en champs contrôlés. Vides = rien n'est écrit : le
  // serveur normalise (`session_history.sanitize_context`).
  const [context, setContext] = useState({
    nb_participants: '', client: '', location: '', notes: '',
  });
  // Une séance d'essai se sait AVANT d'être jouée. Elle reste marquable après
  // coup depuis la fiche de l'historique — le percentile de fin de séance lit
  // `is_test` au moment où il compare, pas au lancement — mais c'est ici que
  // l'animateur y pense.
  const [isTest, setIsTest] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const navigate = useNavigate();
  const { token } = useAuth();

  useEffect(() => {
    const fetchGameConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/get_game_config/${gameConfigId}`);
        if (!response.ok) throw new Error(`Atelier « ${gameConfigId} » introuvable.`);
        const data = await response.json();
        if (!data || !data._id) throw new Error(`Atelier « ${gameConfigId} » introuvable.`);
        setGameConfig(data);
      } catch (error) {
        // Une configuration qui n'arrive pas laissait l'écran sur « Loading… »
        // indéfiniment, sans rien dire.
        setLoadError(error.message);
      }
    };

    fetchGameConfig();
  }, [gameConfigId]);

  const handleActorToggle = (actorId) => {
    setSelectedActors((prev) =>
      prev.includes(actorId) ? prev.filter((id) => id !== actorId) : [...prev, actorId]
    );
  };

  const setField = (key) => (event) =>
    setContext((current) => ({ ...current, [key]: event.target.value }));

  const handleGameStart = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const payload = {
        game_config_id: gameConfigId,
        name: gameName.trim(),
        list_selected_actors: selectedActors,
        is_test: isTest,
        context,
      };
      // LE JETON SIGNE LA SÉANCE (`owner_id`), il n'autorise pas son
      // lancement : `/init_game` reste ouverte, et une séance non signée part
      // quand même — elle n'apparaîtra simplement pas dans l'historique de son
      // animateur (cf. api/session_history.py). Cet écran est derrière
      // `ProtectedRoute`, donc le jeton est là en pratique.
      const response = await fetch(`${process.env.REACT_APP_API_URL}/init_game`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: 'Bearer ' + token } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("L'atelier n'a pas pu démarrer. Réessayez.");

      const result = await response.json();
      navigate('/game/' + result.game_id);
    } catch (error) {
      // Le message part à l'écran : cet écran s'utilise DEVANT LA SALLE, et un
      // bouton qui ne fait rien y est le pire des retours.
      setStartError(error.message);
      setStarting(false);
    }
  };

  if (loadError) {
    return (
      <div className="launcher">
        <div className="launcher-message launcher-message-error">{loadError}</div>
        <button className="history-button history-button-ghost" onClick={() => navigate('/profile')}>
          <FontAwesomeIcon icon={faArrowLeft} /> Retour à mon espace
        </button>
      </div>
    );
  }

  if (!gameConfig) {
    return <div className="launcher"><div className="launcher-message">Chargement de l'atelier…</div></div>;
  }

  const actorOptions = Object.entries(gameConfig.score_dic || {})
    .filter(([, value]) => value.type === 'actor')
    // `description` n'est pas reprise : la liste doit tenir à l'écran en même
    // temps que la fiche, et le rôle de chaque acteur est déjà dans son carnet.
    .map(([key, value]) => ({ id: key, name: value.name, icon: value.icon }));

  const allSelected = actorOptions.length > 0 && selectedActors.length === actorOptions.length;
  const nbPages = (gameConfig.pile || []).length;

  // LE BLOCAGE NE VAUT QUE POUR LES ATELIERS DONT LE BUDGET SORT DES ACTEURS.
  // `vote` et `sum_actor` sont la somme des scores des rôles SÉLECTIONNÉS
  // (game_functions.update_decision_page) : aucun rôle, et le budget reste à
  // zéro toute la partie. Un atelier qui déclare des rôles sans les agréger ne
  // risque rien, et refuser son lancement serait un faux positif.
  const budgetFromActors = Boolean(gameConfig.score_dic?.vote || gameConfig.score_dic?.sum_actor);
  const missingActors = budgetFromActors && actorOptions.length > 0 && selectedActors.length === 0;

  return (
    <div className="launcher">
      <div className="launcher-head">
        <button className="launcher-back" onClick={() => navigate('/profile')}>
          <FontAwesomeIcon icon={faArrowLeft} /> Mon espace
        </button>
        <div className="launcher-eyebrow">Nouvelle séance</div>
        <h1 className="launcher-title">{gameConfig.name}</h1>
        <div className="launcher-facts">
          <span>{dateLabel()}</span>
          {nbPages > 0 && <span>{nbPages} pages</span>}
          {actorOptions.length > 0 && <span>{actorOptions.length} rôles disponibles</span>}
          {gameConfig.territory?.id && <span>territoire {gameConfig.territory.id}</span>}
        </div>
      </div>

      <div className="launcher-grid">
        <section className="launcher-card">
          <div className="launcher-card-head">
            <h2 className="launcher-card-title">La fiche de séance</h2>
            <span className="launcher-optional">facultatif</span>
          </div>
          <p className="launcher-card-hint">
            De quoi retrouver cette séance dans votre historique. Tout reste modifiable
            après coup depuis « Mes séances ».
          </p>

          <div className="launcher-fields">
            <label className="launcher-field launcher-field-wide">
              Nom de la séance
              <input
                type="text"
                value={gameName}
                maxLength={200}
                placeholder={`${gameConfig.name} — ${dateLabel()}`}
                onChange={(e) => setGameName(e.target.value)}
              />
            </label>
            <label className="launcher-field">
              Participants
              <input
                type="number"
                min="0"
                max="10000"
                value={context.nb_participants}
                placeholder="—"
                onChange={setField('nb_participants')}
              />
            </label>
            <label className="launcher-field">
              Client / organisation
              <input type="text" maxLength={200} value={context.client} onChange={setField('client')} />
            </label>
            <label className="launcher-field">
              Lieu
              <input type="text" maxLength={200} value={context.location} onChange={setField('location')} />
            </label>
            <label className="launcher-field launcher-field-wide">
              {/* MÊME CHAMP que « Enseignements de la séance » dans
                  l'historique (`context.notes`), autre intitulé : avant la
                  séance il n'y a pas encore d'enseignement à en tirer. */}
              Notes
              <textarea
                rows={2}
                maxLength={5000}
                value={context.notes}
                placeholder="Contexte, attentes du client, contraintes de salle…"
                onChange={setField('notes')}
              />
            </label>
            <label className="launcher-check">
              <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
              <span>
                Séance de test
                <span className="launcher-check-detail">
                  Exclue des statistiques de votre historique et du classement des séances.
                </span>
              </span>
            </label>
          </div>
        </section>

        {actorOptions.length > 0 && (
          <section className="launcher-card">
            <div className="launcher-card-head">
              <h2 className="launcher-card-title">Les rôles joués</h2>
              <button
                className="launcher-link"
                onClick={() => setSelectedActors(allSelected ? [] : actorOptions.map((a) => a.id))}
              >
                {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
            </div>
            <p className="launcher-card-hint">
              Un rôle par groupe de participants.
              {budgetFromActors
                ? ' Leur nombre fixe le budget de décisions de la partie : il ne se change plus une fois la séance lancée.'
                : ' Ce choix ne se change plus une fois la séance lancée.'}
            </p>

            <div className="launcher-actors">
              {actorOptions.map((actor) => {
                const checked = selectedActors.includes(actor.id);
                return (
                  <label
                    key={actor.id}
                    className={`launcher-actor${checked ? ' launcher-actor-on' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleActorToggle(actor.id)}
                    />
                    {actor.icon && <img src={actor.icon} alt="" className="launcher-actor-icon" />}
                    <span className="launcher-actor-name">{actor.name}</span>
                  </label>
                );
              })}
            </div>

            <div className="launcher-count">
              {selectedActors.length} rôle{selectedActors.length > 1 ? 's' : ''} sur {actorOptions.length}
            </div>
          </section>
        )}
      </div>

      {/* LE BANDEAU DE LANCEMENT EST FIXE, et les avertissements sont DEDANS.
          Le bouton était au bas d'une page qui défile : sur un atelier à six
          rôles, il passait sous le pli, et le message expliquant pourquoi il
          était grisé passait avec lui. Or c'est le seul geste que cet écran
          existe pour faire. La réserve de défilement est posée en CSS
          (`.launcher`), pas ici — cf. la même leçon sur `.game-end-bar`. */}
      <div className="launcher-bar">
        <div className="launcher-bar-inner">
          <div className="launcher-bar-say">
            {startError ? (
              <span className="launcher-bar-error">{startError}</span>
            ) : missingActors ? (
              <span className="launcher-bar-warn">
                Sélectionnez au moins un rôle : sans rôle, la partie démarre avec un budget nul.
              </span>
            ) : (
              <span className="launcher-bar-recap">
                {gameConfig.name}
                {actorOptions.length > 0 && ` · ${selectedActors.length} rôle${selectedActors.length > 1 ? 's' : ''}`}
                {context.nb_participants && ` · ${context.nb_participants} participants`}
                {isTest && ' · séance de test'}
              </span>
            )}
          </div>
          <button
            className="launcher-start"
            onClick={handleGameStart}
            disabled={starting || missingActors}
          >
            <FontAwesomeIcon icon={faPlay} /> {starting ? 'Démarrage…' : "Lancer l'atelier"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameConfigurator;
